import { createContext } from 'react'
import type {
  ShortVideo,
  ShortVideoBookmark,
  ShortVideoChannel,
  ShortVideoLike,
  ShortVideoSubscribe,
  ShortVideoTopic,
} from '@services/firebase/short-videos'

export interface ShortVideosPaginationState {
  readonly canGoNext: boolean
  readonly canGoPrev: boolean
  readonly historySizes: number[]
  readonly offset: number
  readonly total: number
}

export interface ShortVideosState {
  readonly bookmarks: ShortVideoBookmark[]
  readonly channels: ShortVideoChannel[]
  readonly errorMessage: string | null
  readonly likes: ShortVideoLike[]
  readonly feedLoaded: boolean
  readonly loading: {
    readonly bootstrap: boolean
    readonly page: boolean
    readonly search: boolean
  }
  readonly pagination: ShortVideosPaginationState
  readonly searchDialogOpen: boolean
  readonly searchQuery: string
  readonly searchResolvedQuery: string | null
  readonly searchResults: ShortVideo[]
  readonly selectedTopicIds: string[]
  readonly selectedVideo: ShortVideo | null
  readonly subscribes: ShortVideoSubscribe[]
  readonly topics: ShortVideoTopic[]
  readonly videos: ShortVideo[]
}

export interface ShortVideosStateActions {
  readonly setBookmarks: (bookmarks: ShortVideoBookmark[]) => void
  readonly setBootstrapLoading: (isLoading: boolean) => void
  readonly setChannels: (channels: ShortVideoChannel[]) => void
  readonly setErrorMessage: (errorMessage: string | null) => void
  readonly setLikes: (likes: ShortVideoLike[]) => void
  readonly setPageLoading: (isLoading: boolean) => void
  readonly setPagination: (pagination: ShortVideosPaginationState) => void
  readonly setSearchDialogOpen: (isOpen: boolean) => void
  readonly setSearchLoading: (isLoading: boolean) => void
  readonly setSearchQuery: (query: string) => void
  readonly setSearchResults: (results: ShortVideo[]) => void
  readonly setSelectedVideo: (video: ShortVideo | null) => void
  readonly setSubscribes: (subscribes: ShortVideoSubscribe[]) => void
  readonly setTopics: (topics: ShortVideoTopic[]) => void
  readonly setVideos: (videos: ShortVideo[]) => void
  readonly toggleSelectedTopic: (topicId: string) => void
}

export interface ShortVideosStateContextValue {
  readonly actions: ShortVideosStateActions
  readonly state: ShortVideosState
}

export const initialShortVideosState: ShortVideosState = {
  bookmarks: [],
  channels: [],
  errorMessage: null,
  feedLoaded: false,
  likes: [],
  loading: {
    bootstrap: true,
    page: false,
    search: false,
  },
  pagination: {
    canGoNext: false,
    canGoPrev: false,
    historySizes: [],
    offset: 0,
    total: 0,
  },
  searchDialogOpen: false,
  searchQuery: '',
  searchResolvedQuery: null,
  searchResults: [],
  selectedTopicIds: [],
  selectedVideo: null,
  subscribes: [],
  topics: [],
  videos: [],
}

export const ShortVideosStateContext =
  createContext<ShortVideosStateContextValue | null>(null)
